import type { TransformationGraph } from "@shared/types/transformations";

/**
 * #985: the models a run of chosen models builds when "Also build the models they depend on" is
 * ticked, read from the Models view's own graph (`dependsOn`, unique ids). The chosen ones and
 * every model upstream of them (not the ephemeral ones, which dbt does not build), in name order.
 * It is the last run's manifest, so a parent added since then shows once the next run is read. Null when the graph is not there to ask, or does
 * not hold a chosen model, so the dialog never states a count it cannot back.
 */
export function modelsWithParents(
  graph: TransformationGraph | undefined,
  chosen: readonly string[],
): string[] | null {
  if (!graph || chosen.length === 0) return null;
  const nodes = new Map(graph.nodes.map((node) => [node.uniqueId, node]));
  const byName = new Map(
    graph.nodes.filter((node) => node.resourceType === "model").map((node) => [node.name, node]),
  );
  const seen = new Set<string>();
  const stack: string[] = [];
  for (const name of chosen) {
    const node = byName.get(name);
    if (!node) return null;
    stack.push(node.uniqueId);
  }
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const parent of nodes.get(id)?.dependsOn ?? []) stack.push(parent);
  }
  return (
    [...seen]
      .map((id) => nodes.get(id))
      // dbt inlines an ephemeral model and never builds it, so it is walked through but not listed.
      .filter((node) => node?.resourceType === "model" && node.materialized !== "ephemeral")
      .map((node) => node!.name)
      .sort((a, b) => a.localeCompare(b))
  );
}

/** The "Will run" sentence for a run of chosen models. */
export function willRunWords(
  chosen: readonly string[],
  buildParents: boolean,
  withParents: readonly string[] | null,
): string {
  const count = (names: readonly string[]) =>
    `${names.join(", ")} (${names.length} ${names.length === 1 ? "model" : "models"})`;
  if (!buildParents) return count(chosen);
  if (withParents) return count(withParents);
  const one = chosen.length === 1;
  return `${chosen.join(", ")} and the models ${one ? "it depends" : "they depend"} on`;
}
